/**
 * Correcting a trace by hand: making room for a drawn shape, and joining shapes.
 *
 * When the operator draws a unit the tracer missed, they are not adding a shape
 * to a blank part of the sheet — they are correcting one. The ground they draw
 * over is usually already claimed: by the sliver the tracer made of the same
 * unit, or by the neighbour it merged into. Left alone, both shapes exist, they
 * interpenetrate in the 3D model, the traced one paints over the drawn one, and
 * their floor area is counted twice.
 *
 * So a drawn shape TAKES the ground it covers. Anything mostly inside it is
 * replaced outright, and anything clipped by it is cut back to fit around it.
 *
 * The opposite correction is joining. A plan does not always draw the
 * difference between one unit and two: a shopfront line, a mezzanine edge or an
 * old demising wall never cleaned off the drawing all divide a shape the tracer
 * has no way of knowing is whole. Reading the drawing correctly and getting the
 * wrong answer is not a fault that can be fixed by reading harder, so the
 * operator has to be able to say "these are one unit" -- see unionPolygons.
 *
 * The cutting is done on a raster rather than with polygon boolean arithmetic.
 * Exact polygon clipping is a large amount of delicate code — collinear edges,
 * shared vertices, self-intersections all have to be right or it produces
 * garbage silently — and every shape here came off a raster in the first place,
 * at a far coarser resolution than the grid used below. Rasterising, taking the
 * difference and re-tracing reuses machinery that already works and cannot fail
 * in the ways an exact clipper does.
 */

import { simplify, dropCollinear, ensureWinding, signedArea, bounds } from './geometry';

/** Grid resolution along the longer side of the shape being cut. */
const GRID = 384;

/** A cut piece smaller than this share of the original is debris, not a room. */
const MIN_PIECE = 0.02;

function pointInRing(x, y, ring) {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const xi = ring[i][0];
        const yi = ring[i][1];
        const xj = ring[j][0];
        const yj = ring[j][1];
        if (((yi > y) !== (yj > y))
            && (x < (((xj - xi) * (y - yi)) / ((yj - yi) || 1e-12)) + xi)) inside = !inside;
    }
    return inside;
}

/**
 * How much of `subject` lies inside `clip`, 0..1.
 *
 * Sampled on a grid rather than computed exactly, which is all the caller needs:
 * it is deciding between "replace this" and "trim this", and no threshold worth
 * having is sensitive to the third decimal place.
 */
export function coveredFraction(subject, clip) {
    if (!subject || subject.length < 3 || !clip || clip.length < 3) return 0;
    const b = bounds(subject);
    if (!(b.w > 0) || !(b.h > 0)) return 0;

    const n = 64;
    let inside = 0;
    let covered = 0;
    for (let iy = 0; iy < n; iy++) {
        const y = b.minY + ((iy + 0.5) / n) * b.h;
        for (let ix = 0; ix < n; ix++) {
            const x = b.minX + ((ix + 0.5) / n) * b.w;
            if (!pointInRing(x, y, subject)) continue;
            inside += 1;
            if (pointInRing(x, y, clip)) covered += 1;
        }
    }
    return inside ? covered / inside : 0;
}

/** Do two rings share any area at all? Cheap bbox reject, then a sampled test. */
export function overlaps(a, b) {
    if (!a || !b || a.length < 3 || b.length < 3) return false;
    const ba = bounds(a);
    const bb = bounds(b);
    if (ba.minX > bb.maxX || bb.minX > ba.maxX || ba.minY > bb.maxY || bb.minY > ba.maxY) return false;
    return coveredFraction(a, b) > 0;
}

/* Moore neighbourhood, clockwise from due west. */
const MOORE = [
    [-1, 0], [-1, -1], [0, -1], [1, -1],
    [1, 0], [1, 1], [0, 1], [-1, 1],
];

/**
 * Walk the boundary of one connected blob in a boolean grid.
 *
 * The stop condition is a repeated FIRST STEP — same departure cell, same
 * arrival cell — not a repeated cell and not Jacob's criterion. A boundary that
 * pinches to one cell wide is legitimately visited twice, and on some contours
 * the start is re-entered through a different neighbour every lap, so a walk
 * that waits for either of those spirals until it runs out of budget and
 * returns the same ring wound many times over.
 */
function traceBlob(mask, w, h, startIdx) {
    const at = (x, y) => (x < 0 || y < 0 || x >= w || y >= h ? 0 : mask[(y * w) + x]);
    const sx = startIdx % w;
    const sy = (startIdx - sx) / w;

    const ring = [];
    let bx = sx;
    let by = sy;
    let cx = sx - 1;
    let cy = sy;
    let firstToX = -1;
    let firstToY = -1;
    let fromX = -1;
    let fromY = -1;

    const budget = 8 * (w * h);
    for (let step = 0; step < budget; step++) {
        ring.push([bx, by]);

        // Plain loop rather than findIndex: a closure here would capture the
        // walk's mutable cursor, which is both a lint error and a trap.
        let dir = 0;
        for (let d = 0; d < 8; d++) {
            if (bx + MOORE[d][0] === cx && by + MOORE[d][1] === cy) { dir = d; break; }
        }

        let moved = false;
        for (let k = 1; k <= 8; k++) {
            const idx = (dir + k) % 8;
            const nx = bx + MOORE[idx][0];
            const ny = by + MOORE[idx][1];
            if (at(nx, ny)) {
                cx = bx + MOORE[(idx + 7) % 8][0];
                cy = by + MOORE[(idx + 7) % 8][1];
                fromX = bx;
                fromY = by;
                bx = nx;
                by = ny;
                moved = true;
                break;
            }
        }
        if (!moved) break;

        if (firstToX < 0) { firstToX = bx; firstToY = by; }
        else if (fromX === sx && fromY === sy && bx === firstToX && by === firstToY) break;
    }
    return ring;
}

/**
 * A grid covering `b`, surrounded by `pad` empty cells.
 *
 * One cell of pad is the minimum: a blob touching the edge of its grid leaves
 * the boundary walk with nowhere to stand outside it, and the trace comes back
 * wrong. Anything that DILATES the mask needs more, one cell per pass -- with
 * only a single spare cell the growth is clipped at the grid wall while the
 * erosion that follows is not, so a close eats a ring off the outside of the
 * shape instead of leaving it where it was. Measured on two rectangles joined:
 * 2.8% of the floor area, quietly gone.
 */
function gridFor(b, pad = 1) {
    const scale = GRID / Math.max(b.w, b.h);
    const iw = Math.max(8, Math.min(GRID, Math.round(b.w * scale)));
    const ih = Math.max(8, Math.min(GRID, Math.round(b.h * scale)));
    const w = iw + (2 * pad);
    const h = ih + (2 * pad);
    return {
        w,
        h,
        pad,
        toX: (gx) => b.minX + (((gx - pad) + 0.5) / iw) * b.w,
        toY: (gy) => b.minY + (((gy - pad) + 0.5) / ih) * b.h,
        cell: Math.max(b.w / iw, b.h / ih),
    };
}

/** Connected pieces of a mask, biggest first, each as { seed, cells }. */
function piecesOf(mask, w, h) {
    const seen = new Uint8Array(w * h);
    const stack = new Int32Array(w * h);
    const found = [];
    for (let seed = 0; seed < mask.length; seed++) {
        if (!mask[seed] || seen[seed]) continue;
        let sp = 0;
        stack[sp++] = seed;
        seen[seed] = 1;
        const cells = [];
        while (sp > 0) {
            const idx = stack[--sp];
            cells.push(idx);
            const x = idx % w;
            const y = (idx - x) / w;
            if (x > 0 && mask[idx - 1] && !seen[idx - 1]) { seen[idx - 1] = 1; stack[sp++] = idx - 1; }
            if (x < w - 1 && mask[idx + 1] && !seen[idx + 1]) { seen[idx + 1] = 1; stack[sp++] = idx + 1; }
            if (y > 0 && mask[idx - w] && !seen[idx - w]) { seen[idx - w] = 1; stack[sp++] = idx - w; }
            if (y < h - 1 && mask[idx + w] && !seen[idx + w]) { seen[idx + w] = 1; stack[sp++] = idx + w; }
        }
        found.push({ seed, cells });
    }
    found.sort((a, z) => z.cells.length - a.cells.length);
    return found;
}

/** Trace one piece of a mask back into a simplified polygon, or null. */
function polygonOf(piece, grid) {
    const { w, h, toX, toY, cell } = grid;
    const only = new Uint8Array(w * h);
    piece.cells.forEach((i) => { only[i] = 1; });
    const ring = traceBlob(only, w, h, piece.seed);
    if (ring.length < 3) return null;

    // Tolerance of about a cell and a half: enough to take the staircase out of
    // the raster without rounding off a real corner.
    const eps = 1.5 * cell;
    const poly = simplify(ring.map(([gx, gy]) => [toX(gx), toY(gy)]), eps);

    /*
     * Close the ring BEFORE culling collinear vertices.
     *
     * A boundary walk finishes one cell from where it started, and RDP keeps
     * both endpoints, so the ring carries a near-duplicate of its own first
     * vertex. Cull collinear points first and that first vertex is found
     * sitting on the line between its duplicate and the next corner, is
     * deleted as redundant -- and a rectangle comes back as a TRIANGLE, with
     * the right bounding box and half the area. Which is exactly what this did
     * before the merge tool put a plain rectangle through it: every join of two
     * neighbours lost half its floor area, silently, on a figure that goes on a
     * lease.
     */
    if (poly.length > 3) {
        const first = poly[0];
        const last = poly[poly.length - 1];
        if (Math.hypot(first[0] - last[0], first[1] - last[1]) < Math.max(2 * cell, eps)) poly.pop();
    }
    if (poly.length < 3) return null;

    const out = dropCollinear(poly, eps * 0.35);
    if (out.length < 3) return null;
    if (Math.abs(signedArea(out)) < 1e-9) return null;
    return ensureWinding(out);
}

/**
 * `subject` minus `clip`, as a polygon in the same normalised space.
 *
 * Returns null when nothing survives. Where the cut leaves several pieces the
 * largest is kept: that happens only when a drawn shape crosses right through
 * another, which is not a correction anyone makes on purpose, and keeping the
 * biggest piece is a more useful answer than refusing to cut.
 */
export function subtractPolygon(subject, clip) {
    if (!subject || subject.length < 3) return null;
    if (!clip || clip.length < 3) return subject;

    const b = bounds(subject);
    if (!(b.w > 0) || !(b.h > 0)) return null;
    const grid = gridFor(b);
    const { w, h, pad, toX, toY } = grid;

    const mask = new Uint8Array(w * h);
    let any = 0;
    for (let gy = pad; gy < h - pad; gy++) {
        const y = toY(gy);
        for (let gx = pad; gx < w - pad; gx++) {
            const x = toX(gx);
            if (!pointInRing(x, y, subject)) continue;
            if (pointInRing(x, y, clip)) continue;
            mask[(gy * w) + gx] = 1;
            any += 1;
        }
    }
    if (!any) return null;

    const pieces = piecesOf(mask, w, h);
    if (!pieces.length || pieces[0].cells.length / any < MIN_PIECE) return null;
    return polygonOf(pieces[0], grid);
}

/** In-place dilate (or erode) over a 4-neighbourhood, `r` times. */
function morph(mask, w, h, r, dilate) {
    for (let pass = 0; pass < r; pass++) {
        const src = mask.slice();
        for (let y = 1; y < h - 1; y++) {
            for (let x = 1; x < w - 1; x++) {
                const i = (y * w) + x;
                const n = src[i - 1] + src[i + 1] + src[i - w] + src[i + w];
                if (dilate) { if (!src[i] && n) mask[i] = 1; } else if (src[i] && n < 4) mask[i] = 0;
            }
        }
    }
}

/**
 * Join several shapes into one.
 *
 * Neighbouring units meet at the centreline of the wall between them, but only
 * approximately: each outline was simplified on its own, so where they ought to
 * coincide they in fact cross and gap along their shared edge by a fraction of
 * a cell. Rasterising and OR-ing alone therefore leaves a seam of empty cells
 * running through the middle of the result, and the boundary walk dutifully
 * traces around it -- a join with a crack down it.
 *
 * Closing the mask -- dilate, then erode by the same amount -- fills a seam
 * that thin and leaves every gap wider than it alone, so the outer boundary
 * comes back where it started. That is why the pieces are counted AFTER the
 * close: shapes that only failed to touch by rounding are one piece by then,
 * and shapes that genuinely do not touch are still two.
 *
 * Refuses rather than guesses when they are still separate. Quietly keeping the
 * larger piece would delete a unit and look like it had worked, which is the
 * exact failure the review pass exists to stop.
 *
 * @returns {{ polygon, pieces }} `pieces` > 1 means they do not touch, and
 *          `polygon` is null.
 */
export function unionPolygons(polygons, options = {}) {
    const rings = (polygons || []).filter((p) => p && p.length >= 3);
    if (!rings.length) return { polygon: null, pieces: 0 };
    if (rings.length === 1) return { polygon: ensureWinding(rings[0].slice()), pieces: 1 };

    const b = rings.slice(1).reduce((acc, r) => {
        const rb = bounds(r);
        return {
            minX: Math.min(acc.minX, rb.minX),
            minY: Math.min(acc.minY, rb.minY),
            maxX: Math.max(acc.maxX, rb.maxX),
            maxY: Math.max(acc.maxY, rb.maxY),
        };
    }, bounds(rings[0]));
    b.w = b.maxX - b.minX;
    b.h = b.maxY - b.minY;
    if (!(b.w > 0) || !(b.h > 0)) return { polygon: null, pieces: 0 };

    // Room for the close below to grow into and shrink back out of, plus the
    // one spare cell the boundary walk always needs.
    const bridge = options.bridge == null ? 2 : options.bridge;
    const grid = gridFor(b, bridge + 1);
    const { w, h, pad, toX, toY } = grid;
    const mask = new Uint8Array(w * h);
    let any = 0;
    for (let gy = pad; gy < h - pad; gy++) {
        const y = toY(gy);
        for (let gx = pad; gx < w - pad; gx++) {
            const x = toX(gx);
            for (let i = 0; i < rings.length; i++) {
                if (!pointInRing(x, y, rings[i])) continue;
                mask[(gy * w) + gx] = 1;
                any += 1;
                break;
            }
        }
    }
    if (!any) return { polygon: null, pieces: 0 };

    if (bridge > 0) {
        morph(mask, w, h, bridge, true);
        morph(mask, w, h, bridge, false);
    }

    const pieces = piecesOf(mask, w, h);
    // Debris a couple of cells across is the close's own rounding, not a shape.
    const real = pieces.filter((p) => p.cells.length / any >= MIN_PIECE);
    if (!real.length) return { polygon: null, pieces: 0 };
    if (real.length > 1) return { polygon: null, pieces: real.length };

    return { polygon: polygonOf(real[0], grid), pieces: 1 };
}

/**
 * Give a drawn shape the ground it covers.
 *
 * @param {Array} regions       every shape currently on the plan
 * @param {Array} drawn         the ring just drawn, normalised
 * @param {object} options      `replaceAbove` — a shape this covered is replaced
 *                              rather than trimmed
 * @returns {{regions, replaced, trimmed}}
 */
export function claimGround(regions, drawn, options = {}) {
    const replaceAbove = options.replaceAbove == null ? 0.65 : options.replaceAbove;
    const keep = [];
    let replaced = 0;
    let trimmed = 0;

    regions.forEach((r) => {
        if (!r.polygon || r.polygon.length < 3) { keep.push(r); return; }
        const covered = coveredFraction(r.polygon, drawn);
        if (covered <= 0.001) { keep.push(r); return; }

        // Mostly swallowed: this is the shape the operator is replacing.
        if (covered >= replaceAbove) { replaced += 1; return; }

        const cut = subtractPolygon(r.polygon, drawn);
        if (!cut) { replaced += 1; return; }
        trimmed += 1;
        keep.push({
            ...r,
            polygon: cut,
            areaFraction: Math.abs(signedArea(cut)),
            vertices: cut.length,
            trimmedByDrawn: true,
        });
    });

    return { regions: keep, replaced, trimmed };
}
